/* Member administration. A list rather than a table: a person is a name, an address, a capability
   and two controls, and a table of that is a list with lines drawn on it. */

.list,
.roles {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}

.who {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--t-dense);
  font-weight: 650;
  color: var(--ink);
}

.nameLink {
  color: var(--ink);
  text-decoration: none;
}

.nameLink:hover,
.nameLink:focus-visible {
  color: var(--accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.you {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid var(--accent-line);
  border-radius: 999px;
  background: var(--accent-w);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--accent);
}

.you::first-letter {
  text-transform: uppercase;
}

/* Suspended is a **warning**, not a criticality: somebody chose it, and it is reversible. */
.suspended {
  display: inline-block;
  padding: 1px 6px;
  border: 1px solid var(--warn-line);
  border-radius: 999px;
  background: var(--warn-w);
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--warn);
}

.suspended::first-letter {
  text-transform: uppercase;
}

.email {
  font-size: var(--t-min);
  color: var(--muted);
  overflow-wrap: anywhere;
}

/* The capability, not the label. "Approver" tells a reader nothing about what they are granting. */
.can {
  display: block;
  margin-top: 2px;
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--ink-2);
}

.warn {
  margin-top: 4px;
  font-size: var(--t-min);
  line-height: 1.45;
  color: var(--warn);
}

.controls {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.why {
  grid-column: 1 / -1;
  margin-top: 4px;
  padding: 6px 9px;
  border: 1px solid var(--line-2);
  border-left-width: 3px;
  border-radius: 6px;
  background: var(--panel-2);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.roles li {
  padding: 7px 0;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-dense);
  color: var(--ink);
}

.pending {
  margin: 14px 0 0;
  padding: 8px 10px;
  border: 1px dashed var(--line-2);
  border-radius: 7px;
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* ---- Inviting somebody (ADR-0046) and the engagement an Auditor's invitation carries (ADR-0048) */

.form {
  display: grid;
  gap: 10px;
}

.fieldRow {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.field {
  display: grid;
  gap: 4px;
  min-width: 0;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.field input,
.field select {
  padding: 6px 8px;
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
}

/* A refused field is marked by the attribute the browser and a screen reader both read, not by a
   class alone — the colour is the second signal, not the only one. */
.field input[aria-invalid="true"] {
  border-color: var(--crit);
}

.hint {
  font-size: var(--t-min);
  font-weight: 400;
  line-height: 1.45;
  color: var(--muted);
}

.refused {
  margin: 0;
  padding: 6px 9px;
  border: 1px solid var(--crit-line);
  border-left-width: 3px;
  border-radius: 6px;
  background: var(--crit-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--crit);
}

/* The token is shown once, so it is set apart and selectable as a single unit. */
.issued {
  display: grid;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid var(--accent-line);
  border-radius: 7px;
  background: var(--accent-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

.token {
  display: block;
  padding: 6px 8px;
  font-family: var(--mono);
  font-size: var(--t-dense);
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--line-2);
  border-radius: 6px;
  overflow-wrap: anywhere;
  user-select: all;
}

.waiting {
  list-style: none;
  margin: 0;
  padding: 0;
}

.waiting li {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2px 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-dense);
  color: var(--ink);
}

/* The two controls on an outstanding invitation's row sit together; the re-send form opens beneath
   the row and spans it. */
.rowActions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.resendForm {
  grid-column: 1 / -1;
  display: grid;
  gap: 8px;
  padding: 8px 0 2px;
}

/* The filings an Auditor is engaged for: a set of checkboxes, so a group is a fieldset. */
.filings {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid var(--line-2);
  border-radius: 6px;
  font-size: var(--t-dense);
  color: var(--ink);
}

.filings legend {
  padding: 0 4px;
  font-size: var(--t-min);
  font-weight: 600;
  color: var(--ink-2);
}

.filings ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
}

/* One block per company: its name, then the filings owed for it. A heading, not a control. */
.filingGroups {
  display: grid;
  gap: 8px;
}

.filingGroup {
  display: grid;
  gap: 4px;
}

.filingCompany {
  font-size: var(--t-min);
  color: var(--ink-2);
}

.filings[aria-invalid="true"] {
  border-color: var(--crit);
}

.headActions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.drawerStack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* A chip with its info control (and, for an auditor reading every company, the way to fix it). */
.chipRow {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}
